/**
 * The byline — §7.3.
 *
 * "Authors are derived from version history, with manual addition for offline
 * contributors. Nobody maintains a byline by hand." So most of this list is not
 * editable at all: it is a projection of who wrote versions, and the only writes
 * available are the credit for someone who contributed outside the editor and
 * the correction of that credit.
 *
 * **Nothing here grants anything.** §7.3 keeps edit rights in the ACL — "a
 * per-document editor roster would create two systems answering 'can Sarah edit
 * this', and they will disagree" — so a person credited here gets a line in the
 * byline and no access whatsoever. The screen says that in as many words,
 * because a panel headed "Authors" with an "Add" button is otherwise read as
 * exactly the roster the spec refuses to build.
 *
 * Removal only reaches manual credits. A derived author wrote a version, the
 * version still exists, and the projection puts them straight back — which is
 * the correct behaviour and not a bug worth papering over with a filtered view.
 */

import { useState } from 'react';
import { Form } from 'react-aria-components';
import {
  useAddAuthor,
  useDocumentAuthors,
  usePeoplePicker,
  useRemoveAuthor,
} from '../../api/queries.ts';
import type { AuthorView } from '../../api/types.ts';
import { formatRelative } from '../../components/format.ts';
import { Badge, roleLabel, StatusBadge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, EmptyState, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, CardHeader } from '../../components/ui/layout.tsx';

function AuthorRow({
  author,
  isOwner,
  canEdit,
  onRemove,
}: {
  author: AuthorView;
  isOwner: boolean;
  canEdit: boolean;
  onRemove: (personId: string) => void;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-secondary px-5 py-3 last:border-b-0">
      <span className="font-medium text-primary">{author.name ?? 'Unknown person'}</span>
      {isOwner ? <Badge tone="brand">Owner</Badge> : null}
      {author.status ? <StatusBadge status={author.status} /> : null}
      {author.source === 'manual' ? (
        <Badge tone="outline">
          <span title="Credited by hand for a contribution made outside the editor. Grants nothing.">
            credited
          </span>
        </Badge>
      ) : (
        <Badge tone="gray">
          <span title="Derived from version history — nobody typed this name in.">
            {author.versionCount} {author.versionCount === 1 ? 'version' : 'versions'}
          </span>
        </Badge>
      )}
      <span className="ml-auto text-xs text-tertiary">
        {author.lastContributionAt ? `last wrote ${formatRelative(author.lastContributionAt)}` : ''}
      </span>
      {author.note ? <span className="w-full text-xs text-tertiary">{author.note}</span> : null}
      {canEdit && author.source === 'manual' ? (
        <Button
          size="sm"
          variant="tertiary"
          className="text-error-primary"
          onPress={() => onRemove(author.personId)}
        >
          Remove credit
        </Button>
      ) : null}
    </li>
  );
}

function CreditForm({ nodeId, onClose }: { nodeId: string; onClose: () => void }) {
  const add = useAddAuthor(nodeId);
  const picker = usePeoplePicker();
  const [personId, setPersonId] = useState('');
  const [note, setNote] = useState('');

  const options = (picker.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    description: `${person.email} · ${roleLabel(person.role)}`,
  }));

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        add.mutate(
          { personId, ...(note.trim() ? { note: note.trim() } : {}) },
          { onSuccess: onClose },
        );
      }}
    >
      <DialogBody>
        <Alert tone="info" title="This grants nothing">
          Edit rights come from the ACL and only from the ACL (§7.3). Crediting someone here adds a
          line to the byline; it does not let them open, edit, or publish anything.
        </Alert>
        <Select
          label="Person"
          placeholder="Choose a person…"
          options={options}
          selectedKey={personId || null}
          onSelectionChange={setPersonId}
          isRequired
        />
        <TextField
          label="What they contributed"
          value={note}
          onChange={setNote}
          multiline
          rows={2}
          description="Why this person is on the byline when the version history does not show them."
        />
        {add.isError ? <ErrorState error={add.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" isPending={add.isPending} isDisabled={!personId}>
          Credit this person
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function AuthorsCard({ nodeId, canEdit }: { nodeId: string; canEdit: boolean }) {
  const query = useDocumentAuthors(nodeId);
  const remove = useRemoveAuthor(nodeId);
  const [crediting, setCrediting] = useState(false);

  return (
    <Card padded={false}>
      <CardHeader
        title="Authors"
        description="Derived from version history. The owner is singular and lives above."
        actions={
          canEdit ? (
            <Button size="sm" variant="secondary" onPress={() => setCrediting(true)}>
              Credit a contributor
            </Button>
          ) : undefined
        }
      />
      {query.isPending ? (
        <Loading />
      ) : query.isError ? (
        <div className="p-5">
          <ErrorState error={query.error} />
        </div>
      ) : query.data.authors.length === 0 ? (
        <EmptyState
          title="No authors yet"
          description="Nobody has written a version of this document, and nobody has been credited by hand."
        />
      ) : (
        <ul className="flex flex-col">
          {query.data.authors.map((author) => (
            <AuthorRow
              key={author.personId}
              author={author}
              isOwner={author.personId === query.data.ownerId}
              canEdit={canEdit}
              onRemove={(personId) => remove.mutate(personId)}
            />
          ))}
        </ul>
      )}
      {remove.isError ? (
        <div className="p-5">
          <ErrorState error={remove.error} />
        </div>
      ) : null}

      <Dialog
        title="Credit a contributor"
        description="§7.3 — manual addition, for someone who contributed offline."
        isOpen={crediting}
        onOpenChange={setCrediting}
      >
        {(close) => <CreditForm nodeId={nodeId} onClose={close} />}
      </Dialog>
    </Card>
  );
}
